import React, { useState } from 'react';
import { useAuth } from '@/src/context/AuthContext';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';

const Login: React.FC = () => {
  const { login, signUp } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [name, setName] = useState(''); // Only for signup

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (isSignUp) {
        if (!name) throw new Error("Nome é obrigatório para cadastro.");

        // SECURITY: Public signups default to 'operator' to prevent unauthorized admin access.
        // Admins must be created via the internal User Manager or database seeding.
        await signUp({
            name,
            email,
            password,
            role: 'operator',
            sector: 'Geral',
            avatarUrl: `https://ui-avatars.com/api/?name=${name}&background=random`
        });

        // If signUp is successful, AuthContext listener will pick up session change
        if (navigator.vibrate) navigator.vibrate(20);

      } else {
        await login(email, password);
        if (navigator.vibrate) navigator.vibrate(20);
      }
    } catch (err: any) {
      if (navigator.vibrate) navigator.vibrate([50, 50, 50]);
      setError(err.message || "Erro na autenticação.");
      setLoading(false); // Stop loading on error
    }
    // If success, loading stays true until component unmounts/redirects via Context update
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 space-y-8 animate-in fade-in zoom-in-95 duration-500">
        
        <div className="text-center space-y-2">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-indigo-600 text-white mb-2">
                <span className="font-bold text-xl tracking-tighter">AB</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Forms Asa Branca</h1>
            <p className="text-sm text-slate-500">
                {isSignUp ? "Crie sua conta de acesso" : "Entre para acessar seu espaço de trabalho"}
            </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
                {isSignUp && (
                    <div className="animate-in slide-in-from-top-2 fade-in space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Nome Completo</label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Seu nome"
                                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none"
                                required
                            />
                        </div>
                        <div className="bg-yellow-50 text-yellow-800 text-xs p-3 rounded-lg border border-yellow-100 flex gap-2">
                           <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                           <p>Para sua segurança, novas contas são criadas como <strong>Operador</strong>. Para acesso Administrativo, solicite ao setor de TI.</p>
                        </div>
                    </div>
                )}

                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">E-mail</label>
                    <input 
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="seu@email.com"
                        className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none"
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Senha</label>
                    <div className="relative">
                        <input 
                            type={showPassword ? "text" : "password"} 
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none"
                            required
                            minLength={6}
                        />
                        <button 
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-2"
                        >
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm flex items-center gap-2 animate-in slide-in-from-top-2">
                        <AlertCircle size={16} />
                        {error}
                    </div>
                )}

                <button 
                    type="submit"
                    disabled={loading}
                    className="w-full bg-indigo-600 text-white font-bold uppercase tracking-wide py-4 rounded-xl shadow-lg hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                >
                    {loading ? 'Processando...' : (isSignUp ? 'Criar Conta' : 'Entrar no Sistema')}
                </button>
            </div>
            
            <div className="text-center space-y-2">
                <button
                    type="button"
                    onClick={() => { setIsSignUp(!isSignUp); setError(null); }}
                    className="text-sm text-indigo-600 font-semibold hover:underline"
                >
                    {isSignUp ? "Já tem uma conta? Faça Login" : "Não tem conta? Cadastre-se"}
                </button>

                {!isSignUp && (
                    <div className="block">
                        <button type="button" className="text-xs text-slate-400 hover:text-indigo-600 transition-colors">
                            Esqueceu sua senha?
                        </button>
                    </div>
                )}
            </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
